import type { JSX, ForwardedRef, CSSProperties } from 'react';
import { forwardRef, useState, useEffect } from 'react';
import CloseIcon from '@CS/assets/close.svg?component';
import { DELTA_UPDATE } from '@CS/utils/Number';
import Button from '@CS/components/Button';

type Child = JSX.Element | string | null;
type Children<Child> = Child | Child[];

import {
  PopupHeader,
  PopupOverlay,
  PopupContainer,
  TRANSITION_DURATION
} from './Popup.styles';

export type PopupProps = {
  customStyle?: CSSProperties;
  onBeforeClose?: () => void;
  children?: Children<Child>;
  opacity?: number | string;

  onClose?: () => void;
  hideClose?: boolean;
  overlay?: boolean;
  close?: boolean;
  header?: Child;
};

export const Popup = forwardRef(
  (
    {
      onBeforeClose = () => void 0,
      onClose = () => void 0,
      hideClose = false,

      overlay = true,
      opacity = 0.8,
      close = false,

      customStyle,
      children,
      header
    }: PopupProps,
    ref: ForwardedRef<HTMLDialogElement>
  ) => {
    const [visible, setVisible] = useState(close);

    const onPopupClose = () => {
      setTimeout(onClose, TRANSITION_DURATION);
      setVisible(false);
      onBeforeClose();
    };

    useEffect(() => {
      close && onPopupClose();
    }, [close]);

    useEffect(() => {
      setTimeout(() => setVisible(true), DELTA_UPDATE);
    }, []);

    return (
      <PopupOverlay $visible={visible} $overlay={overlay} opacity={opacity}>
        <PopupContainer ref={ref} $low={hideClose} style={customStyle}>
          {header && <PopupHeader>{header}</PopupHeader>}
          {children}
        </PopupContainer>

        {!hideClose && (
          <Button onClick={onPopupClose} round>
            <CloseIcon />
          </Button>
        )}
      </PopupOverlay>
    );
  }
);
